import { useSyncExternalStore } from 'react';

// Lets an admin temporarily view the app as a non-admin user (e.g. to verify
// authorization/UI behavior) without actually changing their AAD role assignment.
const ADMIN_PREVIEW_KEY = 'allphi.adminPreview.nonAdmin';
const ADMIN_PREVIEW_EVENT = 'allphi-admin-preview-changed';

/** Read whether admin preview (viewing as non-admin) is currently enabled. */
function getSnapshot(): boolean {
  return window.sessionStorage.getItem(ADMIN_PREVIEW_KEY) === 'true';
}

/**
 * Subscribe to admin preview changes, both from this tab (custom event) and
 * other tabs (native `storage` event).
 * @param listener - Callback invoked whenever the preview flag may have changed.
 * @returns An unsubscribe function.
 */
function subscribe(listener: () => void): () => void {
  window.addEventListener(ADMIN_PREVIEW_EVENT, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(ADMIN_PREVIEW_EVENT, listener);
    window.removeEventListener('storage', listener);
  };
}

/**
 * React hook returning whether the current admin has enabled "preview as
 * non-admin" mode, reactively updating across tabs.
 * @returns True if admin preview (non-admin view) is enabled.
 */
export function useAdminPreview(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * Enable or disable admin preview mode and notify subscribers.
 * @param enabled - Whether to preview the app as a non-admin user.
 */
export function setAdminPreview(enabled: boolean): void {
  if (enabled) {
    window.sessionStorage.setItem(ADMIN_PREVIEW_KEY, 'true');
  } else {
    window.sessionStorage.removeItem(ADMIN_PREVIEW_KEY);
  }

  window.dispatchEvent(new Event(ADMIN_PREVIEW_EVENT));
}

